import { Link } from "react-router";
import { Eye, HelpCircle, Palette, Plus } from "lucide-react";
import {
  PORTAL_CONTENT_KEYS,
  type PortalContentKey,
  type PortalContentRow,
} from "@colab/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/empty-state";
import { useAdminPortalContent } from "./use-admin-portal-content";
import { PortalContentRows } from "./portal-content-rows";
import { NewPortalContentDialog } from "./new-portal-content-dialog";

// Phase 7 sub-bucket 18 — SPA port of /app/admin/portal-content/page.tsx.
// CMS for welcome / level_up / support_links portal sections + per-scope
// overrides (all / tier / partner_type).

const KEY_META: Record<PortalContentKey, { label: string; description: string }> = {
  welcome: {
    label: "Welcome banner",
    description:
      "Title, body, and CTAs on /portal/dashboard. Supports {partner.first_name} and other merge tokens.",
  },
  level_up: {
    label: "Level-up matrix",
    description:
      "Wrapper copy for the tier matrix on the dashboard. The matrix itself is read from tier programs.",
  },
  support_links: {
    label: "Support links",
    description: "Quick-link cards on /portal/support.",
  },
};

export function PortalContentListPage() {
  const query = useAdminPortalContent();
  const rows = query.data ?? [];

  const grouped: Record<PortalContentKey, PortalContentRow[]> = {
    welcome: [],
    level_up: [],
    support_links: [],
  };
  for (const row of rows) grouped[row.key]?.push(row);

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            Portal content
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Author the partner-facing portal copy: welcome banner, level-up
            matrix, support links, FAQ.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/portal-content/faq">
              <HelpCircle className="mr-1.5 h-3.5 w-3.5" />
              Manage FAQ
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href="/portal/dashboard" target="_blank" rel="noreferrer">
              <Eye className="mr-1.5 h-3.5 w-3.5" />
              Preview portal
            </a>
          </Button>
          <NewPortalContentDialog />
        </div>
      </header>

      {query.isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Palette}
          title="No portal content yet"
          description="Seed the welcome banner, level-up matrix, and support links so partners see something on /portal/dashboard."
          action={<NewPortalContentDialog variant="empty" />}
        />
      ) : (
        <div className="space-y-8">
          {PORTAL_CONTENT_KEYS.map((key) => (
            <section key={key} className="rounded-xl border bg-card">
              <header className="flex items-start justify-between gap-4 border-b p-5">
                <div>
                  <h2 className="font-display text-base font-semibold tracking-tight">
                    {KEY_META[key].label}
                  </h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {KEY_META[key].description}
                  </p>
                </div>
                <NewPortalContentDialog
                  variant="inline"
                  defaultKey={key}
                  label={
                    <>
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      Add variant
                    </>
                  }
                />
              </header>
              <PortalContentRows rows={grouped[key]} contentKey={key} />
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
